import { useState } from "react"

import { Flex, Form, message, Typography, Upload } from "antd"
import { useTranslation } from "react-i18next"

import { AttachmentsList } from "../../Containers/CreditsPage/components/Attachments"
import Icon from "../../Icon"
import { normalizeMoney } from "../../Utils/normalizeMoney"
import ButtonComponent from "../Button"
import InputComponent from "../Input"

const { Text, Link } = Typography

const MAX_TOTAL_SIZE_MB = 10

const validationFields = { file_list: "Please attach at least one file as proof for your request." }

function WithdrawForm({
  onClose,
  onSubmit,
  initialValues,
  isDisabledSubmitBtn,
  isSubmitting = false
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  const [uploadedFiles, setUploadedFiles] = useState([])

  const availableCountFiles = 20

  // Add a flag to track the display of messages
  let hasShownSizeError = false
  let hasShownFormatError = false

  const beforeUpload = (file, fileList) => {
    const allowedExtensions = [
      "png",
      "jpeg",
      "jpg",
      "pdf",
      "txt",
      "doc",
      "docx",
      "odt",
      "rtf",
      "xls",
      "xlsx"
    ]
    const fileExtension = file.name.split(".").pop().toLowerCase()

    // Check the format for each file
    if (!allowedExtensions.includes(fileExtension)) {
      // Show the format error only once per pack
      if (!hasShownFormatError) {
        message.error(`${file.name} has an unsupported format.`)
        hasShownFormatError = true
      }
      return Upload.LIST_IGNORE
    }

    // Reset flags for new size check
    if (fileList.length > 1 && fileList.indexOf(file) === 0) {
      hasShownSizeError = false
      hasShownFormatError = false
    }

    // Calculate the total size
    const currentBatchSize = fileList.reduce((acc, currFile) => acc + (currFile.size || 0), 0)
    const totalSize =
      uploadedFiles.reduce((acc, currFile) => acc + (currFile.size || 0), 0) + currentBatchSize

    if (totalSize / 1024 / 1024 > MAX_TOTAL_SIZE_MB) {
      // Show the size error only once per pack
      if (!hasShownSizeError) {
        message.error(`Total file size should not exceed ${MAX_TOTAL_SIZE_MB} MB.`)
        hasShownSizeError = true
      }
      return Upload.LIST_IGNORE
    }

    return false
  }

  const handleUploadChange = (info) => {
    setUploadedFiles(info.fileList)
  }

  const onFinish = (values) => {
    onSubmit(values)
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
  }

  function onFocusHandler(e, fieldName) {
    const field = fieldName || e.target.name || e.target.id
    handleClearError(e, field)
  }

  const addErrorIcon = (error) => (
    <span>
      <Icon type={"alert_triangle_error"} /> {error}
    </span>
  )

  const validateMessages = {
    required: addErrorIcon("Required field was not filled.")
  }

  const normFile = (e) => {
    if (Array.isArray(e)) return e
    return e?.fileList
  }

  const handleBlur = (e) => form.setFieldValue("amount", normalizeMoney(e.target.value))

  return (
    <Form
      className={"with-default-error"}
      onFinish={onFinish}
      validateTrigger="onSubmit"
      validateMessages={validateMessages}
      layout="vertical"
      form={form}
      {...(initialValues ? { initialValues } : {})}
    >
      <div
      // style={{height: 'min(80vh, 624px)', overflow: 'auto'}}
      >
        <Form.Item name="invoice_id" label={"Invoice ID"} rules={[{ required: true }]}>
          <InputComponent />
        </Form.Item>

        <Form.Item name="amount" label={"Balance withdrawal"} rules={[{ required: true }]}>
          <InputComponent onBlur={handleBlur} />
        </Form.Item>

        <Flex vertical gap={4} style={{ marginBottom: 8 }}>
          <Text>{t("Attach files")}</Text>
          <Text type={"secondary"} style={{ fontWeight: 300 }}>
            Possible applicable file formats: .png, .jpeg, .jpg, .pdf, .txt, .doc, .docx, .odt,
            .rtf, .xls, .xlsx
          </Text>
        </Flex>

        <Form.Item
          label={null}
          name="file_list"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          rules={[
            {
              validator: (_, value) => {
                if (value && value?.length > availableCountFiles) {
                  return Promise.reject(new Error(t("You can upload a maximum of 20 files.")))
                }
                return Promise.resolve()
              }
            },
            {
              required: initialValues?.["attachments"]?.length ? false : true,
              message: t(validationFields["file_list"])
            }
          ]}
        >
          <Upload
            multiple
            maxCount={availableCountFiles}
            accept={".png,.jpeg,.jpg,.pdf,.txt,.doc,.docx,.odt,.rtf,.xls,.xlsx"}
            beforeUpload={beforeUpload}
            onChange={handleUploadChange}
            listType="text"
            // disabled={isAttachDisabled}
            style={{ width: "100%" }}
            iconRender={(file) => (
              <Icon
                type={file?.type?.startsWith("image/") ? "pictures" : "document"}
                style={{ fontSize: 20, lineHeight: 1 }}
              />
            )}
            showUploadList={{
              extra: ({ size = 0 }) => (
                <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
                  ({(size / 1000).toFixed(0)}KB)
                </Text>
              ),
              showRemoveIcon: true,
              removeIcon: <Icon type={"delete"} style={{ fontSize: 20, lineHeight: 1 }} />
            }}
          >
            <ButtonComponent
              size="large"
              type="dashed"
              icon={<Icon type="arrow_out_of_box" />}
              disabled={isDisabledSubmitBtn}
              className={"dashed-btn table-action-btn flex center"}
              block
            >
              <Text type="secondary">{t("Click to upload files")}</Text>
            </ButtonComponent>
          </Upload>
        </Form.Item>

        <AttachmentsList attachments={initialValues?.["attachments"]} />
      </div>
      <Flex gap={16} justify="end" style={{ padding: "32px 3px 3px 3px" }}>
        <ButtonComponent size="large" htmlType="button" onClick={onClose}>
          {t("Cancel")}
        </ButtonComponent>

        <ButtonComponent
          size="large"
          type="primary"
          htmlType="submit"
          loading={isSubmitting}
          disabled={isDisabledSubmitBtn}
        >
          {t("Submit")}
        </ButtonComponent>
      </Flex>
    </Form>
  )
}

export default WithdrawForm
